import { useRef, useEffect, useState } from "react";
import LazyVideo from "@/components/LazyVideo";
import { Star, ShowerHead, Car, Clock, Smartphone, Wifi, ArrowUpRight } from "lucide-react";

function useInView(ref) {
  const [inView, setInView] = useState(false);
  useEffect(() => {const obs = new IntersectionObserver(([e]) => {if (e.isIntersecting) setInView(true);}, { threshold: 0.15 });if (ref.current) obs.observe(ref.current);return () => obs.disconnect();}, [ref]);
  return inView;
}

const ABOUT_VIDEO_URL = "/assets/video/reels.mp4";

const features = [
{ icon: Star, title: "6 campos WPT", desc: "Piso de última geração" },
{ icon: ShowerHead, title: "Balneários", desc: "Cacifos e água quente" },
{ icon: Car, title: "Parque", desc: "Estacionamento grátis" },
{ icon: Clock, title: "7 Dias", desc: "Horário alargado" },
{ icon: Smartphone, title: "App", desc: "Reserva online" },
{ icon: Wifi, title: "WiFi", desc: "Internet grátis" }];


export default function About() {
  const ref = useRef(null);
  const inView = useInView(ref);

  return (
    <section id="about" data-testid="about-section" ref={ref} className="py-24 bg-[#0d1320]">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
        <div className={`${inView ? 'fade-in-up' : 'opacity-0'}`}>
          
          {/* Mobile Layout */}
          <div className="lg:hidden">
            {/* Title */}
            <div className="text-center mb-8">
              <span className="font-body text-xs font-medium uppercase tracking-widest text-jp-lime mb-4 block">Quem Somos</span>
              <h2 className="font-display text-3xl sm:text-4xl text-white font-medium leading-tight">
                A tua segunda casa.
              </h2>
            </div>
            
            {/* Video */}
            <div className="relative w-full mb-8">
              <div className="absolute -inset-4 bg-gradient-to-r from-jp-lime/20 via-transparent to-jp-lime/10 rounded-3xl blur-2xl opacity-50" />
              <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-black/50 w-full">
                <div className="aspect-[9/16]">
                  <LazyVideo
                    src={ABOUT_VIDEO_URL}
                    className="w-full h-full object-cover"
                    data-testid="about-video-mobile" />

                </div>
              </div>
            </div>
            
            {/* Text Content */}
            <p className="font-body text-white/35 text-[15px] leading-relaxed mb-6">
              O Just Padel Center nasceu da paixão por este desporto e pelo desejo de criar um espaço onde toda a gente se sente em casa. Em Barcelos, construímos uma comunidade vibrante de jogadores de todas as idades e níveis.
            </p>
            <p className="font-body text-white/35 text-[15px] leading-relaxed mb-8">
              Dos torneios competitivos às festas de aniversário, das aulas da academia ao convívio no nosso bar - cada momento no Just Padel é especial.
            </p>

            {/* Instalações Mobile */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-8">
              {features.map((f, i) =>
              <div key={f.title} className="bg-[#0a0f1a] border border-white/5 rounded-xl p-4 hover:border-jp-lime/20 transition-colors group" data-testid={`feature-card-mobile-${i}`}>
                  <f.icon className="w-5 h-5 mb-2 text-jp-lime group-hover:scale-110 transition-transform" strokeWidth={1.5} />
                  <div className="font-body font-medium text-sm text-white">{f.title}</div>
                  <div className="font-body text-[11px] text-white/25 mt-0.5">{f.desc}</div>
                </div>
              )}
            </div>

            {/* CTA Button Mobile */}
            <a
              href="#contacts"
              className="w-full bg-jp-lime text-black font-body font-semibold text-sm px-6 py-4 rounded-full hover:bg-jp-lime-dark transition-all flex items-center justify-center gap-2 group"
              data-testid="about-cta-mobile">

              Conhecer clube
              <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </a>
          </div>

          {/* Desktop Layout: Text left, Video right */}
          <div className="hidden lg:grid lg:grid-cols-12 gap-16 items-center">
            {/* Text content on the left */}
            <div className="lg:col-span-7">
              <span className="font-body text-xs font-medium uppercase tracking-widest text-jp-lime mb-4 block">Quem Somos</span>
              <h2 className="font-display text-4xl sm:text-5xl text-white font-medium leading-tight mb-6">
                A tua segunda casa.
              </h2>
              <p className="font-body !text-base mb-6 text-white/35">Mais do que um clube de padel, somos uma comunidade. No Just Padel, todos encontram a sua segunda casa. Um espaço para jogar, evoluir, conviver e criar memórias dentro e fora do campo.

              </p>
              <p className="font-body !text-base mb-10 text-white/35">Dos torneios às festas de aniversário, das aulas da academia ao convívio no nosso bar - cada momento no Just Padel é especial.

              </p>

              {/* Instalações Desktop */}
              <div className="grid grid-cols-3 gap-3 mb-8">
                {features.map((f, i) =>
                <div key={f.title} className="bg-[#0a0f1a] border border-white/5 rounded-xl p-4 hover:border-jp-lime/20 transition-colors group" data-testid={`feature-card-${i}`}>
                    <f.icon className="w-5 h-5 mb-2 text-jp-lime group-hover:scale-110 transition-transform" strokeWidth={1.5} />
                    <div className="font-body font-medium text-sm text-white">{f.title}</div>
                    <div className="font-body !text-sm mt-0.5 text-white/25">{f.desc}</div>
                  </div>
                )}
              </div>

              {/* CTA Button Desktop */}
              <a
                href="#contacts"
                className="inline-flex bg-jp-lime text-black font-body font-semibold text-sm px-8 py-4 rounded-full hover:bg-jp-lime-dark transition-all items-center gap-2 group"
                data-testid="about-cta">

                Conhecer clube
                <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </a>
            </div>
            
            {/* Video on the right */}
            <div className="lg:col-span-5">
              <div className="relative flex justify-end">
                <div className="absolute -inset-4 bg-gradient-to-r from-jp-lime/20 via-transparent to-jp-lime/10 rounded-3xl blur-2xl opacity-50" />
                <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-black/50 max-w-[320px]">
                  <div className="aspect-[9/16]">
                    <LazyVideo
                    src={ABOUT_VIDEO_URL}
                      className="w-full h-full object-cover"
                    data-testid="about-video" />

                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

}